/**
 * The ONLY place in the codebase that converts between hour-position and
 * UTC. Used by api (adapters, optimizer, dispatcher) AND web (Schedule UI
 * needs day length before any data loads, to size the bar grid).
 *
 * Hour-position semantics: `prices[i]` (and `schedule[i]`, `tariff[i]`,
 * `pv[i]`) is the i-th hour of the *local* calendar day in the market's
 * timezone, where day length ∈ {23, 24, 25} due to DST. UTC has no DST,
 * so once we anchor the day's start in UTC, every position is just
 * `dayStartUtc + i*1h` — strictly monotonic, unambiguous.
 *
 * Spring-forward (Kyiv 2026-03-29): 23 hours. Local 03:00–04:00 doesn't
 * exist; positions 0..22 cover 22:00 UTC Mar 28 → 20:00 UTC Mar 29.
 *
 * Fall-back (Kyiv 2026-10-25): 25 hours. Local 03:00–04:00 happens twice
 * (EEST then EET); positions 0..24 cover 21:00 UTC Oct 24 → 21:00 UTC
 * Oct 25.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
const HOUR_MS = 3_600_000;
/** UTC instant when the given local calendar day begins in `tz`. */
export function dayStartUtc(date, tz) {
    const ymd = formatInTimeZone(date, tz, 'yyyy-MM-dd');
    return fromZonedTime(`${ymd}T00:00:00`, tz);
}
function nextDayStartUtc(date, tz) {
    const ymd = formatInTimeZone(date, tz, 'yyyy-MM-dd');
    const [yStr, mStr, dStr] = ymd.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    const nextYmd = formatInTimeZone(next, 'UTC', 'yyyy-MM-dd');
    return fromZonedTime(`${nextYmd}T00:00:00`, tz);
}
export function dayLengthHours(date, tz) {
    const start = dayStartUtc(date, tz);
    const next = nextDayStartUtc(date, tz);
    const hours = Math.round((next.getTime() - start.getTime()) / HOUR_MS);
    if (hours === 23 || hours === 24 || hours === 25)
        return hours;
    throw new Error(`unexpected day length ${hours}h for ${tz} on ${date.toISOString()}`);
}
/** UTC instant when the local calendar day `ymd` (YYYY-MM-DD) begins in
 *  `tz` — from the date itself, so no anchor instant can land on the
 *  neighbouring day (UTC+12 and beyond). */
export function localDayStartUtc(ymd, tz) {
    return fromZonedTime(`${ymd}T00:00:00`, tz);
}
/** Hours of the local calendar day `ymd` in `tz`: 23 / 24 / 25. */
export function localDayLength(ymd, tz) {
    const [y, m, d] = ymd.split('-').map(Number);
    const next = formatInTimeZone(new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + 1)), 'UTC', 'yyyy-MM-dd');
    const hours = Math.round((localDayStartUtc(next, tz).getTime() - localDayStartUtc(ymd, tz).getTime()) / HOUR_MS);
    if (hours === 23 || hours === 24 || hours === 25)
        return hours;
    throw new Error(`unexpected day length ${hours}h for ${tz} on ${ymd}`);
}
/**
 * Fully elapsed hours (positions) of the local day `ymd` at `now`: a past
 * day — all of them, a future day — 0, today — the position of the hour in
 * progress (on a 23/25-hour day this differs from the clock hour).
 */
export function elapsedHoursOfDay(ymd, tz, now = new Date()) {
    const len = localDayLength(ymd, tz);
    const passed = Math.floor((now.getTime() - localDayStartUtc(ymd, tz).getTime()) / HOUR_MS);
    return Math.min(Math.max(passed, 0), len);
}
/**
 * Today's calendar date in the given timezone, as YYYY-MM-DD. The
 * web layer compares this with `?date=` to decide whether the page
 * is in archive (`< today`), live (`= today`), or edit (`> today`)
 * mode. Doing the comparison server-tz-aware avoids a Kyiv user
 * accidentally landing in archive view because their browser
 * happens to be in a westwards timezone.
 */
export function todayInTz(tz) {
    return formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
}
/**
 * Current local hour-of-day (0–23) in the given timezone. Used to decide
 * the "edit floor" on a live (today) schedule: hours strictly before this
 * are already elapsed and locked from editing. Computed tz-aware for the
 * same reason as `todayInTz`.
 */
export function hourOfDayInTz(tz) {
    return Number(formatInTimeZone(new Date(), tz, 'H'));
}
/**
 * Edit floor for a schedule on `date` (YYYY-MM-DD, site-local) in `tz`:
 * the count of leading hours that are read-only. Hours `i < floor` are
 * locked; `i >= floor` are editable.
 *   - date  < today  → whole day locked  (floor = dayLen)
 *   - date  > today  → fully editable    (floor = 0)
 *   - date == today  → elapsed hours only (floor = current local hour;
 *                      the in-progress hour itself stays editable)
 */
export function editFloorForDate(date, tz, dayLen) {
    const today = todayInTz(tz);
    if (date < today)
        return dayLen;
    if (date > today)
        return 0;
    return Math.min(hourOfDayInTz(tz), dayLen);
}
export function hourPositionToUtc(date, tz, position) {
    const len = dayLengthHours(date, tz);
    if (!Number.isInteger(position) || position < 0 || position >= len) {
        throw new RangeError(`position ${position} outside [0, ${len}) for ${tz}`);
    }
    return new Date(dayStartUtc(date, tz).getTime() + position * HOUR_MS);
}
export function utcRangeToPosition(startUtc, date, tz) {
    const start = dayStartUtc(date, tz);
    const diff = (startUtc.getTime() - start.getTime()) / HOUR_MS;
    if (!Number.isInteger(diff)) {
        throw new Error(`utc ${startUtc.toISOString()} not on hour boundary of ${tz} day`);
    }
    const len = dayLengthHours(date, tz);
    if (diff < 0 || diff >= len) {
        throw new RangeError(`utc ${startUtc.toISOString()} outside ${tz} day starting ${start.toISOString()}`);
    }
    return diff;
}
/** Local wall-clock label for a position, used by adapters when serializing TOU slots. */
export function positionToLocalLabel(date, tz, position) {
    const utc = hourPositionToUtc(date, tz, position);
    return formatInTimeZone(utc, tz, 'HH:mm');
}
/**
 * Expand a 24-cell wall-clock template (`values24[hh]` = value at local
 * `HH:00`) into a day-shaped array matching the actual hour count.
 *
 *   24h day — identity copy.
 *   23h day (spring-forward) — drops the wall-clock hour that doesn't
 *     exist (e.g. Kyiv 03:00 on 2026-03-29).
 *   25h day (fall-back) — duplicates the wall-clock hour that occurs
 *     twice (e.g. Kyiv 03:00 on 2026-10-25 happens at both EEST and EET).
 *
 * Used to convert wall-clock-indexed inputs (retail tariff profile,
 * expected-load profile) into the day-shaped arrays consumed by P&L and
 * optimizer code. Putting this conversion at the boundary keeps the
 * downstream invariant simple: every per-hour array equals `dayLengthHours`.
 */
export function expandWallClockToDay(values24, date, tz) {
    if (values24.length !== 24) {
        throw new Error(`expandWallClockToDay expects a 24-cell template, got length ${values24.length}`);
    }
    const len = dayLengthHours(date, tz);
    const out = new Array(len);
    for (let p = 0; p < len; p += 1) {
        const utc = hourPositionToUtc(date, tz, p);
        const hh = Number(formatInTimeZone(utc, tz, 'HH'));
        out[p] = values24[hh] ?? 0;
    }
    return out;
}
/**
 * Stage J: re-shape per-hour setpoints from the source day's length to
 * the target day's length (23/24/25). Used by carry-forward when source
 * and target days have different DST topology.
 *
 * Strategy: we DON'T attempt clever wall-clock alignment — that's only
 * meaningful for retail tariff templates (which are stored as 24-cell
 * wall-clock and expanded via `expandWallClockToDay`). For battery
 * setpoints we just pad with 0 (`hold`) at the tail, or truncate the
 * tail. Rationale: arbitrage windows are usually tight (4-7 hours of
 * charge + 4-7 hours of discharge) and rarely touch the spring-forward /
 * fall-back hour, so tail-pad/truncate gives a sensible result without
 * a per-position remap.
 */
export function adjustHourlyForDayLength(setpoints, targetLen) {
    if (setpoints.length === targetLen)
        return [...setpoints];
    if (setpoints.length > targetLen)
        return setpoints.slice(0, targetLen);
    return [...setpoints, ...new Array(targetLen - setpoints.length).fill(0)];
}
//# sourceMappingURL=time.js.map